import React, { useRef, useState } from 'react';
import api from '../../services/api';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';

/**
 * ImageUploadField — botão de upload local + preview.
 * Props:
 *   value: string (URL atual da imagem)
 *   onChange: (url: string) => void
 *   placeholder: string (texto do botão quando não há imagem)
 *   size: 'sm' | 'md' (tamanho do preview, padrão 'md')
 */
export default function ImageUploadField({ value, onChange, placeholder = 'Adicionar imagem', size = 'md' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const previewSize = size === 'sm' ? 'w-16 h-16' : 'w-24 h-24';

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao enviar imagem');
    } finally {
      setUploading(false);
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-start gap-4">
      {/* Preview / placeholder */}
      <div className={`${previewSize} rounded-xl border-2 border-dashed border-brand-border bg-brand-bg flex items-center justify-center flex-shrink-0 overflow-hidden relative group`}>
        {value ? (
          <>
            <img
              src={value}
              alt="Imagem"
              className="w-full h-full object-cover"
              onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
            <div className="absolute inset-0 bg-black/40 hidden group-hover:flex items-center justify-center">
              <button
                type="button"
                onClick={() => onChange('')}
                className="text-white p-1.5 bg-red-500 rounded-full"
                title="Remover imagem"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </>
        ) : uploading ? (
          <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
        ) : (
          <ImagePlus className="w-6 h-6 text-brand-text-muted/40" />
        )}
      </div>

      {/* Controls */}
      <div className="flex-1 min-w-0">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-brand-border bg-white text-sm font-dashboard text-brand-text hover:bg-brand-bg transition-colors disabled:opacity-50"
          data-testid="upload-imagem-btn"
        >
          <ImagePlus className="w-4 h-4 text-brand-primary" />
          {uploading ? 'Enviando...' : value ? 'Trocar imagem' : placeholder}
        </button>
        <p className="text-xs text-brand-text-muted font-body mt-1">
          JPG, PNG ou WebP · máximo 5MB
        </p>
        {error && <p className="text-xs text-red-600 font-body mt-1">{error}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
          className="hidden"
          onChange={handleFileChange}
          data-testid="upload-imagem-input"
        />
      </div>
    </div>
  );
}
